import { ArrowDown, ArrowUp, Film, ImagePlus, Link2, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { api, errorMessage, type PlaylistItem } from '../api/client';

const mb = (b?: number | null) => (b ? `${(b / 1048576).toFixed(b > 10485760 ? 0 : 1)} MB` : '');

/**
 * The playlist of a local channel: videos and pictures the TV plays one after the other, in a loop,
 * like a broadcast (no player controls). Uploads go straight to the media library.
 */
export function LocalPlaylistEditor({ items, onChange }: { items: PlaylistItem[]; onChange: (items: PlaylistItem[]) => void }) {
  const videoInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addingUrl, setAddingUrl] = useState(false);
  const [url, setUrl] = useState('');

  const upload = async (files: FileList | null, type: 'VIDEO' | 'IMAGE') => {
    if (!files?.length) return;
    setError(null);
    const added: PlaylistItem[] = [];
    const list = [...files];
    for (const [i, f] of list.entries()) {
      setBusy(`Uploading ${i + 1} / ${list.length}: ${f.name}`);
      try {
        const a = await api.upload<{ id: string; url: string; bytes: number }>(type === 'VIDEO' ? '/assets/video' : '/assets', f);
        added.push({ type, assetId: a.id, assetUrl: a.url, bytes: a.bytes, title: f.name.replace(/\.[^.]+$/, ''), durationSec: type === 'IMAGE' ? 10 : null });
      } catch (e) {
        setError(`${f.name}: ${errorMessage(e)}`);
      }
    }
    setBusy(null);
    if (added.length) onChange([...items, ...added]);
  };
  const set = (i: number, p: Partial<PlaylistItem>) => onChange(items.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const move = (i: number, d: -1 | 1) => {
    const n = [...items];
    [n[i], n[i + d]] = [n[i + d]!, n[i]!];
    onChange(n);
  };
  const total = items.reduce((s, i) => s + (i.type === 'IMAGE' ? i.durationSec ?? 10 : 0), 0);

  return (
    <div className="stack">
      {items.length === 0 ? (
        <div className="playlist-empty">
          <Film size={22} />
          <div>Add the videos and pictures this channel plays. They play one after the other and start again from the top, like a TV channel.</div>
        </div>
      ) : (
        <div className="playlist">
          {items.map((it, i) => (
            <div key={i} className="playlist-item">
              <span className="playlist-num">{i + 1}</span>
              <span className="playlist-thumb">
                {it.type === 'IMAGE' && (it.assetUrl || it.url) ? <img src={it.assetUrl ?? it.url ?? ''} alt="" /> : <Film size={20} />}
              </span>
              <span className="playlist-info">
                <input className="playlist-title" value={it.title ?? ''} placeholder={it.type === 'VIDEO' ? 'Video' : 'Picture'} onChange={(e) => set(i, { title: e.target.value })} />
                <span className="muted playlist-meta">
                  {it.type === 'VIDEO' ? 'Video' : 'Picture'}
                  {it.url && !it.assetId ? ` · ${it.url}` : ''}
                  {it.bytes ? ` · ${mb(it.bytes)}` : ''}
                </span>
              </span>
              {it.type === 'IMAGE' ? (
                <label className="playlist-dur">
                  <input type="number" min={1} max={3600} value={it.durationSec ?? 10} onChange={(e) => set(i, { durationSec: Math.max(1, Number(e.target.value) || 10) })} />
                  <span className="muted">s</span>
                </label>
              ) : (
                <span className="muted playlist-dur">to the end</span>
              )}
              <button className="icon-btn" title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                <ArrowUp size={15} />
              </button>
              <button className="icon-btn" title="Move down" disabled={i === items.length - 1} onClick={() => move(i, 1)}>
                <ArrowDown size={15} />
              </button>
              <button className="icon-btn" title="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
      {addingUrl && (
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <input placeholder="https://…/video.mp4 or .m3u8" value={url} onChange={(e) => setUrl(e.target.value)} style={{ flex: 1 }} autoFocus />
          <button
            type="button"
            className="btn small primary"
            disabled={!/^https?:\/\//i.test(url)}
            onClick={() => {
              onChange([...items, { type: 'VIDEO', url, title: url.split('/').pop()?.split('?')[0] ?? 'Video' }]);
              setUrl('');
              setAddingUrl(false);
            }}
          >
            Add
          </button>
          <button type="button" className="btn small" onClick={() => setAddingUrl(false)}>
            Cancel
          </button>
        </div>
      )}
      <div className="row">
        <button type="button" className="btn small" disabled={!!busy} onClick={() => videoInput.current?.click()}>
          <Upload size={14} /> Upload videos
        </button>
        <button type="button" className="btn small" disabled={!!busy} onClick={() => imageInput.current?.click()}>
          <ImagePlus size={14} /> Upload pictures
        </button>
        <button type="button" className="btn small" disabled={!!busy} onClick={() => setAddingUrl(true)}>
          <Link2 size={14} /> Video address
        </button>
        {items.length > 0 && (
          <span className="muted" style={{ marginLeft: 'auto', fontSize: 12.5 }}>
            {items.length} items{total ? ` · pictures ${total}s` : ''}
          </span>
        )}
      </div>
      <div className="muted" style={{ fontSize: 12 }}>Videos: MP4 (H.264 video, AAC audio), up to 100 MB each. Pictures: 1920×1080 JPEG or PNG.</div>
      {busy && <div className="progress-line">{busy}…</div>}
      {error && <div className="error">{error}</div>}
      <input ref={videoInput} type="file" accept="video/mp4,.mp4,.m4v" multiple hidden onChange={(e) => { void upload(e.target.files, 'VIDEO'); e.target.value = ''; }} />
      <input ref={imageInput} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => { void upload(e.target.files, 'IMAGE'); e.target.value = ''; }} />
    </div>
  );
}
